import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, TrendingUp, TrendingDown, Users, Package, DollarSign,
  BarChart3, AlertTriangle, CheckCircle, ArrowUpRight, ArrowDownRight,
  Clock, Target, Zap, FileText, Calculator, RefreshCw
} from 'lucide-react';
import { authService } from '../services/auth';
import apiClient from '../services/api';
import { toast } from 'sonner';

const WINDOW_OPTIONS = [
  { value: '3m', label: '3 Month' },
  { value: '6m', label: '6 Month' },
  { value: '12m', label: '12 Month' },
  { value: '18m', label: '18 Month' },
];

const SEGMENT_COLORS = {
  vip: '#10b981',
  growth: '#3b82f6',
  stable: '#8b5cf6',
  at_risk: '#f59e0b',
  unprofitable: '#ef4444',
  new: '#6b7280',
};

const QUADRANT_COLORS = {
  star: '#10b981',
  cash_cow: '#3b82f6',
  question_mark: '#f59e0b',
  dog: '#ef4444',
};

export default function FinancialHealthPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [cashflowWindow, setCashflowWindow] = useState('6m');
  const [cashflow, setCashflow] = useState(null);
  const [cashflowLoading, setCashflowLoading] = useState(false);
  const [mcaForm, setMcaForm] = useState({ principal: '', factor_rate: '', term_months: '6', payment_frequency: 'daily' });
  const [mcaResult, setMcaResult] = useState(null);
  const [mcaAnalyzing, setMcaAnalyzing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => { loadDashboard(); }, []);
  useEffect(() => { loadCashflow(); }, [cashflowWindow]);

  const loadDashboard = async () => {
    try {
      const profile = await authService.getProfile();
      const brandId = profile.member?.merchant_id;
      if (!brandId) return;

      const res = await apiClient.get(`/api/financial-health/dashboard?brand_id=${brandId}`);
      setDashboard(res.data);
    } catch (err) {
      console.error('Failed to load financial health dashboard:', err);
      if (err.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const loadCashflow = async () => {
    setCashflowLoading(true);
    try {
      const profile = await authService.getProfile();
      const brandId = profile.member?.merchant_id;
      if (!brandId) return;

      const res = await apiClient.get(`/api/financial-health/cashflow?brand_id=${brandId}&window=${cashflowWindow}`);
      setCashflow(res.data);
    } catch (err) {
      console.error('Failed to load cashflow:', err);
    } finally {
      setCashflowLoading(false);
    }
  };

  const analyzeMCA = async () => {
    if (!mcaForm.principal || !mcaForm.factor_rate) {
      toast.error('Enter principal and factor rate');
      return;
    }
    setMcaAnalyzing(true);
    try {
      const res = await apiClient.post('/api/financial-health/mca/analyze', {
        principal: parseFloat(mcaForm.principal),
        factor_rate: parseFloat(mcaForm.factor_rate),
        term_months: parseInt(mcaForm.term_months),
        payment_frequency: mcaForm.payment_frequency,
      });
      setMcaResult(res.data);
    } catch (err) {
      toast.error('MCA analysis failed');
    } finally {
      setMcaAnalyzing(false);
    }
  };

  const runFullAnalysis = async () => {
    try {
      const profile = await authService.getProfile();
      const brandId = profile.member?.merchant_id;
      if (!brandId) return;

      toast.info('Running full profitability analysis...');
      await Promise.allSettled([
        apiClient.get(`/api/financial-health/customers?brand_id=${brandId}`),
        apiClient.get(`/api/financial-health/products?brand_id=${brandId}`),
      ]);
      toast.success('Analysis complete');
      loadDashboard();
    } catch (err) {
      toast.error('Analysis failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
      </div>
    );
  }

  const resilience = dashboard?.resilience || {};
  const customerSummary = dashboard?.customer_summary || {};
  const productSummary = dashboard?.product_summary || {};
  const brief = dashboard?.latest_brief;

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield className="h-7 w-7 text-emerald-400" />
              Financial Health Suite
            </h1>
            <p className="text-gray-400 mt-1">
              Proactive profitability intelligence — never need predatory financing again
            </p>
          </div>
          <button
            onClick={runFullAnalysis}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-sm font-medium transition"
          >
            <RefreshCw className="h-4 w-4" /> Run Analysis
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 mb-6 bg-gray-900 rounded-lg p-1 w-fit">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'customers', label: 'Customers', icon: Users },
            { id: 'products', label: 'Products', icon: Package },
            { id: 'cashflow', label: 'Cashflow', icon: TrendingUp },
            { id: 'mca', label: 'MCA Protection', icon: Shield },
            { id: 'brief', label: 'CFO Brief', icon: FileText },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition ${
                activeTab === tab.id
                  ? 'bg-emerald-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              <tab.icon className="h-4 w-4" /> {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Resilience Score */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="col-span-1 bg-gray-900 rounded-xl p-6 border border-gray-800">
                <div className="text-center">
                  <p className="text-sm text-gray-400 mb-2">Resilience Score</p>
                  <div className={`text-5xl font-bold ${
                    resilience.score >= 80 ? 'text-emerald-400' :
                    resilience.score >= 60 ? 'text-yellow-400' :
                    resilience.score >= 40 ? 'text-orange-400' : 'text-red-400'
                  }`}>
                    {resilience.score || 0}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">out of 100</p>
                  <span className={`inline-block mt-3 px-3 py-1 rounded-full text-xs font-medium ${
                    resilience.mca_risk_level === 'low' ? 'bg-emerald-900/50 text-emerald-400 border border-emerald-800' :
                    resilience.mca_risk_level === 'moderate' ? 'bg-yellow-900/50 text-yellow-400 border border-yellow-800' :
                    resilience.mca_risk_level === 'high' ? 'bg-orange-900/50 text-orange-400 border border-orange-800' :
                    'bg-red-900/50 text-red-400 border border-red-800'
                  }`}>
                    {resilience.mca_risk_level || 'unknown'} MCA risk
                  </span>
                </div>
              </div>

              {/* Score Components */}
              {resilience.components && Object.entries(resilience.components).map(([key, val]) => (
                <div key={key} className="bg-gray-900 rounded-xl p-5 border border-gray-800">
                  <p className="text-sm text-gray-400 capitalize">{key.replace(/_/g, ' ')}</p>
                  <p className="text-3xl font-bold mt-2">{val}</p>
                  <div className="mt-3 w-full bg-gray-800 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${val >= 70 ? 'bg-emerald-500' : val >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                      style={{ width: `${val}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Recommendations */}
            {resilience.recommendations?.length > 0 && (
              <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Target className="h-5 w-5 text-emerald-400" /> Recommendations
                </h3>
                <div className="space-y-3">
                  {resilience.recommendations.map((rec, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-gray-800/50 rounded-lg">
                      <CheckCircle className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-gray-300">{rec}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                label="Customer Segments"
                value={customerSummary.segment_counts ? Object.values(customerSummary.segment_counts).reduce((a, b) => a + b, 0) : 0}
                sub="total customers"
                icon={Users}
              />
              <StatCard
                label="Products Analyzed"
                value={productSummary.analyzed_products || productSummary.total_products || 0}
                sub="in BCG matrix"
                icon={Package}
              />
              <StatCard
                label="Star Products"
                value={productSummary.quadrant_counts?.star || 0}
                sub="high margin + velocity"
                icon={Zap}
                color="text-emerald-400"
              />
              <StatCard
                label="At-Risk Customers"
                value={customerSummary.segment_counts?.at_risk || 0}
                sub="need win-back"
                icon={AlertTriangle}
                color="text-orange-400"
              />
            </div>
          </div>
        )}

        {/* Customers Tab */}
        {activeTab === 'customers' && (
          <div className="space-y-6">
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
              <h3 className="text-lg font-semibold mb-4">Customer Segments</h3>
              {customerSummary.segment_counts ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {Object.entries(customerSummary.segment_counts).map(([seg, count]) => (
                    <div key={seg} className="text-center p-4 bg-gray-800/50 rounded-lg">
                      <div className="w-3 h-3 rounded-full mx-auto mb-2" style={{ backgroundColor: SEGMENT_COLORS[seg] || '#6b7280' }} />
                      <p className="text-2xl font-bold">{count}</p>
                      <p className="text-xs text-gray-400 capitalize mt-1">{seg.replace(/_/g, ' ')}</p>
                      {customerSummary.segment_revenue?.[seg] && (
                        <p className="text-xs text-gray-500 mt-1">
                          ${(customerSummary.segment_revenue[seg] || 0).toLocaleString()}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">Run analysis to see customer segments</p>
              )}
            </div>

            {/* Top Customers */}
            {customerSummary.top_customers?.length > 0 && (
              <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                <h3 className="text-lg font-semibold mb-4">Top VIP Customers</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-400 border-b border-gray-800">
                        <th className="pb-3">Customer</th>
                        <th className="pb-3">Revenue</th>
                        <th className="pb-3">Orders</th>
                        <th className="pb-3">AOV</th>
                        <th className="pb-3">CLV</th>
                        <th className="pb-3">Segment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customerSummary.top_customers.map((c, i) => (
                        <tr key={i} className="border-b border-gray-800/50">
                          <td className="py-3 font-medium">{c.customer_id || c.customer_email}</td>
                          <td className="py-3">${(c.total_revenue || 0).toLocaleString()}</td>
                          <td className="py-3">{c.order_count}</td>
                          <td className="py-3">${(c.avg_order_value || 0).toFixed(0)}</td>
                          <td className="py-3 text-emerald-400">${(c.clv_estimate || 0).toLocaleString()}</td>
                          <td className="py-3">
                            <span className="px-2 py-1 rounded text-xs" style={{ backgroundColor: SEGMENT_COLORS[c.segment] + '20', color: SEGMENT_COLORS[c.segment] }}>
                              {c.segment}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="space-y-6">
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
              <h3 className="text-lg font-semibold mb-4">BCG Product Matrix</h3>
              {productSummary.quadrant_counts ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { key: 'star', label: 'Stars', desc: 'High margin, high velocity' },
                    { key: 'cash_cow', label: 'Cash Cows', desc: 'High margin, low velocity' },
                    { key: 'question_mark', label: 'Question Marks', desc: 'Low margin, high velocity' },
                    { key: 'dog', label: 'Dogs', desc: 'Low margin, low velocity' },
                  ].map(q => (
                    <div key={q.key} className="p-5 bg-gray-800/50 rounded-lg border-l-4" style={{ borderColor: QUADRANT_COLORS[q.key] }}>
                      <p className="text-sm text-gray-400">{q.label}</p>
                      <p className="text-3xl font-bold mt-1">{productSummary.quadrant_counts[q.key] || 0}</p>
                      <p className="text-xs text-gray-500 mt-1">{q.desc}</p>
                      <p className="text-sm text-gray-400 mt-2">
                        ${(productSummary.quadrant_revenue?.[q.key] || 0).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">Run analysis to see product quadrants</p>
              )}
            </div>

            {/* Margin Improvement Plan */}
            {productSummary.margin_improvement_plan?.length > 0 && (
              <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-emerald-400" /> Margin Improvement Plan
                </h3>
                <div className="space-y-3">
                  {productSummary.margin_improvement_plan.map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
                      <div>
                        <p className="font-medium">{item.product}</p>
                        <p className="text-sm text-gray-400">
                          {item.action === 'raise_price' ? `Raise price ${item.suggested_increase}` : 'Discontinue'}
                          {item.current_margin && ` (current margin: ${item.current_margin})`}
                        </p>
                      </div>
                      <div className="text-right">
                        {item.estimated_monthly_gain ? (
                          <p className="text-emerald-400 font-medium">
                            +${item.estimated_monthly_gain.toLocaleString()}/mo
                          </p>
                        ) : item.monthly_loss ? (
                          <p className="text-red-400 font-medium">
                            saving ${item.monthly_loss.toLocaleString()}/mo
                          </p>
                        ) : null}
                        <span className={`text-xs px-2 py-0.5 rounded ${item.priority === 'high' ? 'bg-red-900/50 text-red-400' : 'bg-yellow-900/50 text-yellow-400'}`}>
                          {item.priority}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Cashflow Tab */}
        {activeTab === 'cashflow' && (
          <div className="space-y-6">
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold">Cashflow Forecast</h3>
                <div className="flex gap-1 bg-gray-800 rounded-lg p-1">
                  {WINDOW_OPTIONS.map(w => (
                    <button
                      key={w.value}
                      onClick={() => setCashflowWindow(w.value)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                        cashflowWindow === w.value
                          ? 'bg-emerald-600 text-white'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      {w.label}
                    </button>
                  ))}
                </div>
              </div>

              {cashflowLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-500" />
                </div>
              ) : cashflow ? (
                <div className="space-y-4">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <MiniStat label="Total Inflow" value={`$${(cashflow.total_projected_inflow || 0).toLocaleString()}`} />
                    <MiniStat label="Total Outflow" value={`$${(cashflow.total_projected_outflow || 0).toLocaleString()}`} />
                    <MiniStat label="Net Position" value={`$${(cashflow.net_position || 0).toLocaleString()}`} color={cashflow.net_position >= 0 ? 'text-emerald-400' : 'text-red-400'} />
                    <MiniStat label="Runway" value={`${cashflow.runway_weeks || 0} weeks`} />
                    <MiniStat label="Confidence" value={`${((cashflow.confidence_avg || 0) * 100).toFixed(0)}%`} />
                  </div>

                  {/* Funding Gaps */}
                  {cashflow.funding_gaps?.length > 0 && (
                    <div className="p-4 bg-red-900/20 border border-red-800/50 rounded-lg">
                      <p className="text-sm font-medium text-red-400 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        {cashflow.funding_gaps.length} funding gap{cashflow.funding_gaps.length > 1 ? 's' : ''} detected
                      </p>
                      <div className="mt-2 space-y-1">
                        {cashflow.funding_gaps.slice(0, 5).map((gap, i) => (
                          <p key={i} className="text-xs text-gray-400">
                            Week {gap.week} ({gap.week_start}): ${gap.shortfall.toLocaleString()} shortfall
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Week-by-week table (compact) */}
                  {cashflow.weeks?.length > 0 && (
                    <div className="overflow-x-auto max-h-96">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-gray-900">
                          <tr className="text-left text-gray-400 border-b border-gray-800">
                            <th className="pb-2 pr-4">Week</th>
                            <th className="pb-2 pr-4">Inflow</th>
                            <th className="pb-2 pr-4">Outflow</th>
                            <th className="pb-2 pr-4">Net</th>
                            <th className="pb-2 pr-4">Cumulative</th>
                            <th className="pb-2">Confidence</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cashflow.weeks.map((w, i) => (
                            <tr key={i} className={`border-b border-gray-800/30 ${w.cumulative_position < 0 ? 'bg-red-900/10' : ''}`}>
                              <td className="py-1.5 pr-4 font-medium">W{w.week_number}</td>
                              <td className="py-1.5 pr-4 text-emerald-400">${w.projected_inflow?.toLocaleString()}</td>
                              <td className="py-1.5 pr-4 text-red-400">${w.projected_outflow?.toLocaleString()}</td>
                              <td className={`py-1.5 pr-4 ${w.projected_net >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                ${w.projected_net?.toLocaleString()}
                              </td>
                              <td className={`py-1.5 pr-4 font-medium ${(w.cumulative_position || 0) >= 0 ? 'text-white' : 'text-red-400'}`}>
                                ${w.cumulative_position?.toLocaleString()}
                              </td>
                              <td className="py-1.5 text-gray-500">{((w.confidence || 0) * 100).toFixed(0)}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 text-sm text-center py-8">Select a window to generate forecast</p>
              )}
            </div>
          </div>
        )}

        {/* MCA Protection Tab */}
        {activeTab === 'mca' && (
          <div className="space-y-6">
            {/* MCA Analyzer */}
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Calculator className="h-5 w-5 text-emerald-400" /> MCA Debt Analyzer
              </h3>
              <p className="text-sm text-gray-400 mb-4">
                Enter your MCA terms to see the true cost. Most MCAs disguise APRs of 40-350% behind "factor rates."
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Principal ($)</label>
                  <input
                    type="number"
                    value={mcaForm.principal}
                    onChange={e => setMcaForm({ ...mcaForm, principal: e.target.value })}
                    placeholder="50000"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Factor Rate</label>
                  <input
                    type="number"
                    step="0.01"
                    value={mcaForm.factor_rate}
                    onChange={e => setMcaForm({ ...mcaForm, factor_rate: e.target.value })}
                    placeholder="1.35"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Term (months)</label>
                  <input
                    type="number"
                    value={mcaForm.term_months}
                    onChange={e => setMcaForm({ ...mcaForm, term_months: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Payment Frequency</label>
                  <select
                    value={mcaForm.payment_frequency}
                    onChange={e => setMcaForm({ ...mcaForm, payment_frequency: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
              </div>

              <button
                onClick={analyzeMCA}
                disabled={mcaAnalyzing}
                className="px-6 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-medium transition disabled:opacity-50"
              >
                {mcaAnalyzing ? 'Analyzing...' : 'Reveal True Cost'}
              </button>

              {/* MCA Result */}
              {mcaResult && (
                <div className="mt-6 p-5 bg-gray-800/50 rounded-lg border border-gray-700">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-gray-400">True APR</p>
                      <p className={`text-2xl font-bold ${
                        mcaResult.effective_apr >= 50 ? 'text-red-400' :
                        mcaResult.effective_apr >= 36 ? 'text-orange-400' : 'text-yellow-400'
                      }`}>
                        {mcaResult.effective_apr}%
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Total Repayment</p>
                      <p className="text-2xl font-bold text-red-400">${mcaResult.total_repayment?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Total Cost of Borrowing</p>
                      <p className="text-2xl font-bold text-red-400">${mcaResult.total_cost?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Daily Payment</p>
                      <p className="text-2xl font-bold">${mcaResult.daily_payment?.toLocaleString()}</p>
                    </div>
                  </div>

                  {/* Predatory Level Badge */}
                  <div className={`inline-block px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide ${
                    mcaResult.predatory_level === 'predatory' ? 'bg-red-900/50 text-red-400 border border-red-700' :
                    mcaResult.predatory_level === 'danger' ? 'bg-orange-900/50 text-orange-400 border border-orange-700' :
                    mcaResult.predatory_level === 'warning' ? 'bg-yellow-900/50 text-yellow-400 border border-yellow-700' :
                    'bg-emerald-900/50 text-emerald-400 border border-emerald-700'
                  }`}>
                    {mcaResult.predatory_level}
                  </div>

                  {/* Flags */}
                  {mcaResult.predatory_flags?.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {mcaResult.predatory_flags.map((flag, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm text-red-300">
                          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-red-400" />
                          {flag}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* CI Alternative */}
                  {mcaResult.savings_vs_mca > 0 && (
                    <div className="mt-4 p-4 bg-emerald-900/20 border border-emerald-800/50 rounded-lg">
                      <p className="text-sm font-medium text-emerald-400">
                        Commerce Index alternative at {mcaResult.ci_alternative_apr}% APR would save you{' '}
                        <span className="text-lg font-bold">${mcaResult.savings_vs_mca?.toLocaleString()}</span>
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Monthly payment: ${mcaResult.ci_alternative_monthly?.toLocaleString()}/mo
                        vs ${mcaResult.monthly_payment?.toLocaleString()}/mo with current MCA
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Resilience Score (also on MCA tab) */}
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
              <h3 className="text-lg font-semibold mb-2">Never Need an MCA Score</h3>
              <p className="text-sm text-gray-400 mb-4">
                Score 80+ means your business is strong enough to never need predatory financing.
              </p>
              <div className="flex items-center gap-6">
                <div className={`text-6xl font-bold ${
                  resilience.score >= 80 ? 'text-emerald-400' :
                  resilience.score >= 60 ? 'text-yellow-400' :
                  resilience.score >= 40 ? 'text-orange-400' : 'text-red-400'
                }`}>
                  {resilience.score || 0}
                </div>
                <div className="flex-1 space-y-2">
                  {resilience.components && Object.entries(resilience.components).map(([key, val]) => (
                    <div key={key} className="flex items-center gap-3">
                      <span className="text-xs text-gray-400 w-40 capitalize">{key.replace(/_/g, ' ')}</span>
                      <div className="flex-1 bg-gray-800 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${val >= 70 ? 'bg-emerald-500' : val >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{ width: `${val}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium w-8">{val}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CFO Brief Tab */}
        {activeTab === 'brief' && (
          <div className="space-y-6">
            {brief ? (
              <>
                <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">{brief.headline || 'Weekly Financial Brief'}</h3>
                    <span className="text-xs text-gray-500">{brief.period}</span>
                  </div>

                  {/* Key Metrics */}
                  {brief.key_metrics && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                      {Object.entries(brief.key_metrics).map(([key, val]) => (
                        <div key={key} className="p-3 bg-gray-800/50 rounded-lg">
                          <p className="text-xs text-gray-400 capitalize">{key.replace(/_/g, ' ')}</p>
                          <p className="text-lg font-bold mt-1">
                            {typeof val === 'number' ? (val > 100 ? `$${val.toLocaleString()}` : val) : val}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Top Actions */}
                  {brief.top_actions?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-300 mb-3">Priority Actions</h4>
                      <div className="space-y-2">
                        {brief.top_actions.map((action, i) => (
                          <div key={i} className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg">
                            <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                              action.priority === 'critical' ? 'bg-red-900/50 text-red-400' :
                              action.priority === 'high' ? 'bg-orange-900/50 text-orange-400' :
                              'bg-blue-900/50 text-blue-400'
                            }`}>
                              {action.priority}
                            </span>
                            <p className="text-sm text-gray-300">{action.action || action.description}</p>
                            {action.estimated_impact && (
                              <span className="ml-auto text-xs text-emerald-400">{action.estimated_impact}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Insights Sections */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {brief.customer_insights && Object.keys(brief.customer_insights).length > 0 && (
                    <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                      <h4 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
                        <Users className="h-4 w-4" /> Customer Insights
                      </h4>
                      {Object.entries(brief.customer_insights).map(([key, val]) => (
                        <div key={key} className="flex justify-between py-2 border-b border-gray-800/50 text-sm">
                          <span className="text-gray-400 capitalize">{key.replace(/_/g, ' ')}</span>
                          <span>{typeof val === 'number' ? val.toLocaleString() : String(val)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {brief.product_insights && Object.keys(brief.product_insights).length > 0 && (
                    <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                      <h4 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
                        <Package className="h-4 w-4" /> Product Insights
                      </h4>
                      {Object.entries(brief.product_insights).map(([key, val]) => (
                        <div key={key} className="flex justify-between py-2 border-b border-gray-800/50 text-sm">
                          <span className="text-gray-400 capitalize">{key.replace(/_/g, ' ')}</span>
                          <span>{typeof val === 'number' ? val.toLocaleString() : String(val)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="bg-gray-900 rounded-xl p-12 border border-gray-800 text-center">
                <FileText className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No CFO brief generated yet</p>
                <p className="text-sm text-gray-500 mt-1">Run a full analysis to generate your first weekly brief</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, color = 'text-white' }) {
  return (
    <div className="bg-gray-900 rounded-xl p-5 border border-gray-800">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-400">{label}</p>
        <Icon className="h-5 w-5 text-gray-600" />
      </div>
      <p className={`text-2xl font-bold mt-2 ${color}`}>{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  );
}

function MiniStat({ label, value, color = 'text-white' }) {
  return (
    <div className="p-3 bg-gray-800/50 rounded-lg">
      <p className="text-xs text-gray-400">{label}</p>
      <p className={`text-lg font-bold mt-1 ${color}`}>{value}</p>
    </div>
  );
}
